import Big from "big.js";
import { makeAutoObservable } from "mobx";
import type ExpenseItemModule from "../../data/ExpenseItemModel";

export class ExpenseFormState {
  amount: Big;
  paidByUserId: string;
  title?: string = undefined;
  splits: { userId: string; amount: Big; touched: boolean }[] = [];
  splitBy: "AMOUNT" | "SHARES" = "AMOUNT";

  constructor(
    initialUserUid: string,
    initialSplits?: { userId: string; amount: number }[],
    protected expense?: ExpenseItemModule,
  ) {
    this.amount = new Big(expense?.data.amount ?? 0);

    if (expense) {
      this.title = expense.data.title;
    }

    this.paidByUserId = expense?.data.paidByUserId ?? initialUserUid;
    this.splits = (expense?.data.splits ?? initialSplits ?? []).map(
      (split) => ({ ...split, amount: new Big(split.amount), touched: false }),
    );

    makeAutoObservable(this);
  }

  setPaidByUserId(userId: string) {
    this.paidByUserId = userId;
  }

  setTitle(title: string) {
    this.title = title;
  }

  setSplitBy(splitBy: "AMOUNT" | "SHARES") {
    this.splitBy = splitBy;
  }

  setAmount(amount: number) {
    if (!this.amount.eq(amount)) {
      this.amount = new Big(amount);
      this.distributeAmountEqually();
    }
  }

  distributeAmountEqually() {
    if (!this.splits.length) {
      return;
    }

    const equalAmount = this.amount.div(this.splits.length);
    this.splits.forEach((split) => {
      split.amount = equalAmount;
      split.touched = false;
    });
  }

  setSplit(userId: string, amount?: Big | number) {
    const newAmount = new Big(amount ?? 0); // Default to 0 if amount is undefined
    const splitIndex = this.splits.findIndex(
      (split) => split.userId === userId,
    );
    const totalCurrentAmount = this.splits.reduce(
      (sum, split) => sum.plus(split.amount),
      new Big(0),
    );

    if (splitIndex >= 0) {
      const oldAmount = this.splits[splitIndex].amount;
      this.splits[splitIndex].amount = newAmount;
      this.splits[splitIndex].touched = true;

      if (newAmount.lte(this.amount)) {
        const totalAfterUpdate = totalCurrentAmount
          .minus(oldAmount)
          .plus(newAmount);
        if (totalAfterUpdate !== this.amount) {
          this.adjustOtherSplits(splitIndex);
        }
      } else {
        // If new amount exceeds the "Main amount", all other splits should be set to 0
        this.splits.forEach((split, idx) => {
          split.amount = idx === splitIndex ? newAmount : new Big(0);
        });
        this.amount = newAmount; // Update the "Main amount" if the new split is larger
      }
    } else {
      // Adding a new split
      this.splits.push({ userId, amount: newAmount, touched: true });
      this.distributeAmountEqually();
    }
  }

  adjustOtherSplits(excludedIndex: number) {
    // Calculate the total amount contributed by touched splits
    const totalTouchedAmount = this.splits.reduce(
      (sum, split) => sum.plus(split.touched ? split.amount : 0),
      new Big(0),
    );

    // Determine if all splits have been touched
    const allTouched = this.splits.every((split) => split.touched);

    // Calculate the remaining amount that needs to be distributed to untouched splits
    const remainingAmount = this.amount.minus(totalTouchedAmount);

    // Get all untouched splits except the one currently being updated
    const otherSplits = this.splits.filter(
      (split, index) => index !== excludedIndex && !split.touched,
    );

    // Check if distributing the remaining amount among untouched splits would result in negative values
    if (remainingAmount.lt(0) && otherSplits.length > 0) {
      // If remaining amount is negative, adjust the main amount to be the sum of all touched splits
      this.amount = totalTouchedAmount;
      // Set all other splits to 0 as there is no remaining amount to distribute
      otherSplits.forEach((split) => {
        split.amount = new Big(0);
      });
    } else if (otherSplits.length > 0) {
      // Distribute the remaining amount equally among untouched splits
      const equalAmountForUntouched = remainingAmount.div(otherSplits.length);
      otherSplits.forEach((split) => {
        split.amount = equalAmountForUntouched; // Adjust and round to two decimal places
      });

      // Apply the recalculated amounts to the main splits array
      this.splits.forEach((split, index) => {
        if (index !== excludedIndex && !split.touched) {
          split.amount =
            otherSplits.find((s) => s.userId === split.userId)?.amount ??
            new Big(0);
        }
      });
    } else if (allTouched && totalTouchedAmount !== this.amount) {
      // If all splits are touched and their total doesn't match the main amount, update the main amount
      this.amount = totalTouchedAmount;
    }
  }

  recalculateTotalAmount() {
    const totalSplitAmount = this.splits.reduce(
      (sum, split) => sum.plus(split.amount),
      new Big(0),
    );
    this.amount = totalSplitAmount; // Ensure main amount always matches the sum of splits
  }

  removeSplit(userId: string) {
    this.splits = this.splits.filter((split) => split.userId !== userId);
    this.distributeAmountEqually();
  }

  get usersSplitInfo() {
    const includedUsers = this.splits.filter((split) => split.amount.gt(0));
    const numberOfUsers = includedUsers.length;

    if (!this.amount.gt(0) || !numberOfUsers) {
      return undefined;
    }

    const equalSplitAmount = this.amount.div(numberOfUsers);
    const isAmountSplitEqually = includedUsers.every((split) =>
      split.amount.eq(equalSplitAmount),
    );

    return {
      count: numberOfUsers,
      equalSplitAmount,
      equallyDistributed: isAmountSplitEqually,
    };
  }

  toObject() {
    return {
      amount: this.amount,
      title: this.title,
      splits: this.splits,
      paidByUserId: this.paidByUserId,
    };
  }
}
